import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Lock, AlertCircle, CheckCircle2 } from 'lucide-react';

/**
 * Reset-password screen — clean EG3 brand chrome (iter114s.68).
 *
 * Replaces the previous medieval theme (knight artwork +
 * Cormorant Garamond italics + "Inscribe A New Word" copy) with the
 * standard EG3 surface — pale-green gradient backdrop, the circular
 * EG3 logo, lucide icons, plain conversational copy.
 *
 * Token comes via `?token=...` from the email link and is pulled
 * fresh from useSearchParams() at submit time so a stray React
 * DevTools snapshot doesn't leak it. Functional logic and all
 * data-testids are preserved.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = (params.get('token') || '').trim();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fail-fast state when the URL is malformed (no token at all).
  if (!token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-[#E8F1E6] via-[#F4F7F2] to-white"
        data-testid="reset-page-missing-token"
      >
        <div className="max-w-md text-center bg-white rounded-2xl shadow-xl p-8 border border-[#E5E7EB]">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-2xl text-[#14281D] font-semibold mb-2">
            Reset link missing
          </h1>
          <p className="text-[#1F3D2C]/75 mb-5">
            This page requires a reset link from your email. Please request a new one to continue.
          </p>
          <Link
            to="/forgot-password"
            className="inline-block bg-[#6FBF73] hover:bg-[#4A7C59] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
            data-testid="reset-request-new-link"
          >
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError("Passwords don't match. Please try again.");
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2400);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 429) {
        setError('Too many attempts. Please wait 15 minutes and try again.');
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#E8F1E6] via-[#F4F7F2] to-white flex items-center justify-center px-4 py-10"
      data-testid="reset-password-page-medieval"
    >
      <div className="w-full max-w-md">
        {/* Logo + title */}
        <div className="text-center mb-6">
          <Link to="/login" data-testid="reset-logo-link" className="inline-block">
            <img
              src="/eg3-logo.png"
              alt="Evergreen Graphics Group"
              className="h-20 w-20 mx-auto mb-3 rounded-full ring-2 ring-[#6FBF73]/30 object-contain bg-white"
            />
          </Link>
          <h1
            className="text-3xl md:text-4xl font-bold text-[#14281D] tracking-tight"
            data-testid="reset-heading"
          >
            Set a new password
          </h1>
          <p
            className="mt-2 text-sm md:text-base text-[#1F3D2C]/75"
            data-testid="reset-subheading"
          >
            Choose a new password and confirm it.
          </p>
        </div>

        {/* Form card */}
        <div
          className="bg-white rounded-2xl shadow-xl p-7 border border-[#E5E7EB]"
          data-testid="reset-form-card"
        >
          {success ? (
            <div data-testid="reset-success-state" className="text-center py-2">
              <CheckCircle2 className="w-12 h-12 text-[#4A7C59] mx-auto mb-3" />
              <h2 className="text-xl text-[#14281D] font-semibold mb-1">
                Password updated successfully.
              </h2>
              <p className="text-sm text-[#1F3D2C]/75">
                Redirecting you to sign in…
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div
                  className="p-3.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700 text-sm"
                  data-testid="reset-error-banner"
                >
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label
                  className="block text-sm font-medium text-[#14281D] mb-1.5"
                  htmlFor="reset-pw"
                >
                  New password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  <input
                    id="reset-pw"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    required
                    autoComplete="new-password"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent text-base"
                    data-testid="reset-password-input"
                  />
                </div>
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-[#14281D] mb-1.5"
                  htmlFor="reset-pw2"
                >
                  Confirm password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  <input
                    id="reset-pw2"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter your password"
                    required
                    autoComplete="new-password"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent text-base"
                    data-testid="reset-confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#6FBF73] hover:bg-[#4A7C59] text-white py-3 text-base font-semibold rounded-full transition-colors"
                data-testid="reset-submit-button"
              >
                {loading ? 'Saving…' : 'Save new password'}
              </Button>

              <div className="text-center pt-2">
                <Link
                  to="/login"
                  className="text-[#2F5D3A] hover:text-[#6FBF73] hover:underline font-medium text-sm"
                  data-testid="reset-cancel-link"
                >
                  ← Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
